import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { APP_EVENTS } from '../../../core/constants/events';
import { api } from '../../../core/network/api';
import { queryClient } from '../../../core/network/query-client';
import { holdsPermission, readAccount } from './permissions';

const BOOT_TIMEOUT_MS = 10_000;
const EMPTY = new Set();

const authPaths = {
    me: () => 'auth/me',
    login: () => 'auth/login',
    logout: () => 'auth/logout',
};

const AuthContext = createContext(null);

// Core has no GET /auth/permissions: /auth/me returns the user package with
// its resolved permission codes, so one call bootstraps both.
async function fetchSession() {
    try {
        const { data } = await api.get(authPaths.me(), { timeout: BOOT_TIMEOUT_MS });
        return { user: readAccount(data), error: null };
    } catch (error) {
        if (error?.status === 401) return { user: null, error: null };
        return { user: null, error };
    }
}

export function AuthProvider({ children }) {
    const [session, setSession] = useState({ initializing: true, user: null, error: null });

    const refresh = useCallback(async () => {
        const next = await fetchSession();
        setSession({ initializing: false, ...next });
    }, []);

    const retry = useCallback(async () => {
        setSession((current) => ({ ...current, initializing: true, error: null }));
        await refresh();
    }, [refresh]);

    const login = useCallback(async ({ email, password }) => {
        await api.post(authPaths.login(), { email: email.trim().toLowerCase(), password });
        await refresh();
    }, [refresh]);

    const logout = useCallback(async () => {
        try {
            await api.post(authPaths.logout());
        } catch {
            // Logout must succeed locally even if the server is unreachable.
        }
        queryClient.clear();
        setSession({ initializing: false, user: null, error: null });
    }, []);

    useEffect(() => {
        let isActive = true;
        (async () => {
            const next = await fetchSession();
            if (isActive) setSession({ initializing: false, ...next });
        })();
        return () => {
            isActive = false;
        };
    }, []);

    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        const handleExpired = () => refresh();
        window.addEventListener(APP_EVENTS.SESSION_EXPIRED, handleExpired);
        window.addEventListener(APP_EVENTS.PERMISSIONS_REFRESH, handleExpired);
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            window.removeEventListener(APP_EVENTS.SESSION_EXPIRED, handleExpired);
            window.removeEventListener(APP_EVENTS.PERMISSIONS_REFRESH, handleExpired);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [refresh]);

    const value = useMemo(() => {
        const permissions = new Set(Array.isArray(session.user?.permissions) ? session.user.permissions : []);
        const denied = new Set(Array.isArray(session.user?.denied) ? session.user.denied : []);
        const can = (domain) => holdsPermission(permissions, domain) && !denied.has(domain);
        return {
            user: session.user,
            isAuthenticated: !!session.user,
            initializing: session.initializing,
            bootError: session.error,
            permissions: session.user ? permissions : EMPTY,
            denied,
            can,
            canAny: (...domains) => domains.some(can),
            canAll: (...domains) => domains.every(can),
            refreshPermissions: refresh,
            retry,
            login,
            logout,
        };
    }, [session, refresh, retry, login, logout]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
    return context;
}
